import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import client from "@/lib/api";
import { fileSrc } from "@/components/common";
import {
  LayoutDashboard, Users, Shield, Dumbbell, Swords, Trophy, Wallet, Package,
  FileText, Bot, UserRound, Goal, BarChart3, Bell, UserCog, Settings, Trash2,
  Menu, Search, Sun, Moon, Languages, LogOut, ChevronDown, Upload, Terminal, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/players", key: "players", icon: Users, roles: ["admin", "finance_manager", "coach", "assistant_coach"] },
  { to: "/teams", key: "teams", icon: Shield, roles: ["admin", "coach", "assistant_coach"] },
  { to: "/training", key: "training", icon: Dumbbell, roles: ["admin", "coach", "assistant_coach"] },
  { to: "/matches", key: "matches", icon: Swords, roles: ["admin", "coach", "assistant_coach"] },
  { to: "/tournaments", key: "tournaments", icon: Trophy, roles: ["admin", "coach"] },
  { to: "/finance", key: "finance", icon: Wallet, roles: ["admin", "finance_manager"] },
  { to: "/inventory", key: "inventory", icon: Package, roles: ["admin"] },
  { to: "/documents", key: "documents", icon: FileText, roles: ["admin", "coach"] },
  { to: "/ai", key: "ai_assistant", icon: Bot, roles: ["admin", "finance_manager", "coach"] },
  { to: "/ai-command", key: "ai_command", icon: Terminal, roles: ["admin"] },
  { to: "/portal/coach", key: "my_teams", icon: Goal, roles: ["coach", "assistant_coach"] },
  { to: "/portal/parent", key: "my_players", icon: UserRound, roles: ["parent"] },
  { to: "/parents", key: "parents", icon: UserRound, roles: ["admin"] },
  { to: "/coaches", key: "coaches", icon: Goal, roles: ["admin"] },
  { to: "/reports", key: "reports", icon: BarChart3, roles: ["admin", "finance_manager"] },
  { to: "/notifications", key: "notifications", icon: Bell, roles: "all" },
  { to: "/users", key: "users", icon: UserCog, roles: ["admin"] },
  { to: "/settings", key: "settings", icon: Settings, roles: ["admin"] },
  { to: "/trash", key: "trash", icon: Trash2, roles: ["admin"] },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setRes(null); return; }
    const id = setTimeout(() => client.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
      <Input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players, ID, phone..." className="ps-9" />
      {res && res.players?.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg overflow-hidden" data-testid="search-results">
          {res.players.map((p) => (
            <button key={p.id} className="w-full text-start px-3 py-2 hover:bg-muted text-sm flex justify-between" onClick={() => { nav(`/players/${p.id}`); setQ(""); setRes(null); }}>
              <span>{p.full_name}</span><span className="text-muted-foreground text-xs">{p.player_code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, lang, setLang, theme, setTheme, t, can } = useApp();
  const [openMobile, setOpenMobile] = useState(false);
  const [brand, setBrand] = useState({});
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles === "all" || can(n.roles));

  useEffect(() => { client.get("/settings/public").then((r) => setBrand(r.data)).catch(() => {}); }, []);
  const logoSrc = brand.logo_url ? (brand.logo_url.startsWith("/api") ? fileSrc(brand.logo_url) : brand.logo_url) : "/logo.png";
  const academyName = brand.academy_name || "Touzani Academy";

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-[#0F172A] text-slate-100 flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
        <img src={logoSrc} alt={academyName} className="h-9 w-9 object-contain" />
        <span className="font-extrabold text-base font-heading tracking-tight leading-tight">{academyName}</span>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-3 space-y-0.5">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpenMobile(false)} data-testid={`nav-${n.key}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-emerald-500 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
            <n.icon size={18} /> <span>{t(n.key)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 text-xs text-slate-400">{user?.role_label}</div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="hidden lg:block">{Sidebar}</div>
      {openMobile && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpenMobile(false)} />
          <div className="absolute inset-y-0 start-0">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border bg-card flex items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpenMobile(true)} data-testid="mobile-menu"><Menu size={20} /></Button>
          <GlobalSearch />
          <div className="flex items-center gap-1 ms-auto">
            <Button variant="ghost" size="icon" onClick={() => setLang(lang === "en" ? "ar" : "en")} data-testid="lang-toggle" title="Language"><Languages size={18} /><span className="text-xs ms-0.5">{lang.toUpperCase()}</span></Button>
            <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "light" ? "dark" : "light")} data-testid="theme-toggle">{theme === "light" ? <Moon size={18} /> : <Sun size={18} />}</Button>
            <Button variant="ghost" size="icon" onClick={() => nav("/notifications")} data-testid="notif-bell"><Bell size={18} /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted" data-testid="user-menu">
                  <div className="h-8 w-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold">{user?.name?.[0]?.toUpperCase()}</div>
                  <ChevronDown size={14} className="hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/settings")}><Settings size={15} className="me-2" /> {t("settings")}</DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-rose-600" data-testid="logout-btn"><LogOut size={15} className="me-2" /> {t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
